import React, { useMemo, useState } from "react";
import { FiActivity, FiAlertCircle } from "react-icons/fi";
import { EmptyState } from "../../../common/comms/EmptyState";
import { useUtils } from "../../../context/UtilsContext";
import DayDetail from "../../hormones/ui/DayDetail";
import DetailSection from "../../hormones/ui/DetailSection";
import KpiStrip from "../../hormones/ui/KpiStrip";
import MetricTabs from "../../hormones/ui/MetricTabs";
import WellnessCalendar from "../../hormones/ui/WellnessCalendar";
import WellnessTrend from "../../hormones/ui/WellnessTrend";
import { useGutData } from "../core/api/useGutData";
import { useGutFibre } from "../core/api/useGutFibre";
import { bristolLookup } from "../core/model/bowel";
import { buildContext } from "../core/model/dayIndex";
import { fmy } from "../core/model/dates";
import { GUT_TABS } from "../core/model/palette";
import { buildView, fibreMonths } from "../core/model/screen";
import BowelPattern from "../ui/BowelPattern";
import ScoreCards from "../ui/ScoreCards";
import Segmented from "../ui/Segmented";
import WeekHeader from "../ui/WeekHeader";

/** The slide-in for a tab switch; none before the first switch. */
const slideClass = (from) =>
  from === "right" ? "motion-safe:animate-tab-in-right" : from === "left" ? "motion-safe:animate-tab-in-left" : "";

const errorMessage = (error) =>
  error?.response?.data?.detail || error?.response?.data?.message || error?.message || "Something went wrong.";

const Spinner = () => (
  <div className="flex justify-center items-center py-24 w-full">
    <div role="status" aria-label="Loading" className="w-12 h-12 border-4 border-dashed rounded-full animate-spin border-orange-primary" />
  </div>
);

/**
 * Gut Health — a read-only view of the client's gut symptom and bowel logs
 * from the Flex app, with fibre from their diet tracking. Recreates
 * `Gut Health v12.dc.html` (see the feature README).
 *
 * @param {Object} props
 * @param {string} props.profileId - The client's UserProfile id.
 */
const GutHealthTab = ({ profileId }) => {
  const { data, today, loading, error, refresh } = useGutData(profileId);
  const { profileUtils } = useUtils() || {};
  const lookup = useMemo(() => bristolLookup(profileUtils?.bristol_stool_types), [profileUtils]);
  // Selected day (selects its week), compare, calendar month (undefined = the
  // selected day's), open sections, and the Bowel pattern's own view/compare.
  const [state, setState] = useState({ tab: "sym", sel: undefined, cmp: "prev", month: undefined, open: {}, bcView: "week", bcCmp: "prev", nut: "fib" });
  const [slideFrom, setSlideFrom] = useState("");
  const set = (patch) => setState((s) => ({ ...s, ...patch }));

  // Without fibre: enough to know the first log and which months of diet
  // tracking the view needs. Fibre then loads behind, a month at a time.
  const base = useMemo(() => (data ? buildContext(data, { bristolLookup: lookup }) : null), [data, lookup]);
  const months = useMemo(() => (base?.hasData ? fibreMonths(base, state) : []), [base, state]);
  const fibre = useGutFibre(profileId, months, { today, historyStart: data?.historyStart });
  const ctx = useMemo(() => (base?.hasData ? buildContext(data, { bristolLookup: lookup, fibre }) : null), [base, data, lookup, fibre]);
  const view = useMemo(() => (ctx ? buildView(ctx, state) : null), [ctx, state]);
  const since = base?.firstLog ?? null;

  const shell = (children, header = null) => (
    <div className="w-full min-w-0 font-dmsans antialiased text-[#1f1f1f] bg-[#FAF9F8] rounded-[14px] px-[28px] pt-[24px] pb-[36px] flex flex-col gap-[18px]">
      <div className="flex justify-between items-center gap-[16px] flex-wrap">
        <div className="flex flex-col gap-[2px]">
          <span className="text-[20px] font-bold">Gut Health</span>
          {since && <span className="text-[13px] text-[#6b6b6b]">Tracking since {fmy(since)}</span>}
        </div>
        {header}
      </div>
      {children}
    </div>
  );

  if (loading) return shell(<Spinner />);
  if (error && !data) {
    return shell(
      <EmptyState
        icon={<FiAlertCircle className="w-12 h-12 text-gray-400 mx-auto" />}
        title="Couldn’t load gut health data"
        hint={errorMessage(error)}
        action={
          <button type="button" onClick={refresh} className="px-4 py-2 rounded-lg bg-[#1f1f1f] text-white text-sm font-semibold">
            Try again
          </button>
        }
      />
    );
  }
  if (!view) {
    return shell(
      <EmptyState
        icon={<FiActivity className="w-12 h-12 text-gray-400 mx-auto" />}
        title="No gut logs yet"
        hint="Symptom, bowel and fibre insights appear once the client logs gut symptoms or a bowel movement in the Flex app."
      />
    );
  }

  const { tab } = state;
  const pickTab = (id) => {
    if (id === tab) return;
    const order = (t) => GUT_TABS.findIndex((x) => x.id === t);
    setSlideFrom(order(id) > order(tab) ? "right" : "left");
    set({ tab: id });
  };
  // Selecting a day (calendar, week stepper) selects its week, and the calendar follows it.
  const selectDay = (iso) => set({ sel: iso, month: undefined });

  return shell(
    <>
      <ScoreCards
        cards={view.cards}
        onPick={(c) => {
          pickTab(c.tab);
          if (c.nut) set({ nut: c.nut });
        }}
      />

      <div className="flex justify-between items-center gap-[16px] flex-wrap">
        <MetricTabs tabs={GUT_TABS} active={tab} onPick={pickTab} label="Daily Log category" />
        <span className="text-[12px] text-[#6b6b6b] pt-[6px]">Daily Log · {view.scope}</span>
      </div>

      {/* Keyed by tab so each switch replays the slide-in. */}
      <div
        key={tab}
        role="tabpanel"
        aria-label={GUT_TABS.find((t) => t.id === tab)?.name}
        className={`flex flex-col gap-[18px] ${slideClass(slideFrom)}`}
      >
        <KpiStrip primary={view.primary} secondary={view.secondary} lists={view.lists} />

        {tab === "nut" && (
          <div className="flex items-center gap-[10px] -mb-[6px]">
            <span className="text-[12px] font-semibold text-[#6b6b6b]">Show on calendar and trend</span>
            <Segmented options={view.nutOpts} onPick={(k) => set({ nut: k })} label="Nutrition measure" />
          </div>
        )}

        <div className="bg-white border border-[#ECECEC] rounded-[12px] grid grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)] overflow-hidden">
          <div className="px-[22px] py-[20px]">
            <WellnessCalendar
              cal={view.cal}
              showPhases={false}
              navLabels={["Show previous month", "Show next month"]}
              onPrev={() => set({ month: view.monthIndex - 1 })}
              onNext={() => set({ month: view.monthIndex + 1 })}
              onSelect={(iso) => set({ sel: iso })}
            />
          </div>
          <div className="border-l border-[#F1ECE8]">
            <DayDetail detail={view.detail} />
          </div>
        </div>

        {view.bowel && (
          <BowelPattern
            bc={view.bowel}
            onSelect={selectDay}
            onView={(v) => set({ bcView: v })}
            onCompare={(k) => set({ bcCmp: k })}
          />
        )}

        <div className="min-w-0 bg-white border border-[#ECECEC] rounded-[12px] px-[22px] py-[20px]">
          <WellnessTrend trend={view.trend} size="md" />
        </div>

        {view.details.map((d) => (
          <DetailSection
            key={d.id}
            d={d}
            bodyKey={d.id}
            bodyClass=""
            onRange={() => {}}
            onToggle={() => set({ open: { ...state.open, [`${tab}-${d.id}`]: !d.isOpen } })}
          />
        ))}
      </div>

      <span className="text-[11px] text-[#8a8a8a] leading-[1.5] [text-wrap:pretty]">{view.footnote}</span>
    </>,
    <WeekHeader header={view.header} onSelect={selectDay} onCompare={(k) => set({ cmp: k })} />
  );
};

export default GutHealthTab;
